import React, { useContext, useEffect } from "react";
import {
  Box, Button, CircularProgress, FormControl, FormErrorMessage, FormLabel,
  Grid, GridItem, Heading, HStack, IconButton, Input, InputGroup,
  InputRightAddon, SimpleGrid, Text,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import { useFormik } from "formik";
import CreatableSelect from "react-select/creatable";
import FormInput from "../../components/form/FormInput";
import { GlobalContext } from "../../context/Provider";
import drugsList from "../../utils/drugsList";
import { getAgeApp } from "../../utils/getAge";
import { MoneyInput } from "./PaymentSection";

const emptyInjection = () => ({ drug: "", dosage: "", amount: "", discount: 0, totalAmount: 0 });
const number = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
};
const lineTotal = (item) => Math.round(
  Math.max(number(item.amount) - ((number(item.amount) * number(item.discount)) / 100), 0) * 100,
) / 100;
const currency = (value) => new Intl.NumberFormat("en-NG", {
  style: "currency", currency: "NGN", minimumFractionDigits: 2,
}).format(number(value));

const Injection = ({ form, onSubmitAttachment, bioData, onPaymentChange }) => {
  const { authState } = useContext(GlobalContext);
  const formik = useFormik({
    initialValues: form || {
      surname: bioData?.surname || "",
      otherNames: bioData?.otherNames || "",
      age: bioData?.dateOfBirth ? getAgeApp(bioData.dateOfBirth) : "",
      unitNo: bioData?.unitNo || "",
      date: new Date().toLocaleDateString(),
      physician: `${authState.user?.firstName || ""} ${authState.user?.lastName || ""}`.trim(),
      injectionItems: [emptyInjection()],
    },
    validate: (values) => {
      const itemErrors = (values.injectionItems || []).map((item) => ({
        ...(!String(item.drug || "").trim() ? { drug: "Drug is required" } : {}),
        ...(!String(item.dosage || "").trim() ? { dosage: "Dosage is required" } : {}),
        ...(item.amount === "" || item.amount == null ? { amount: "Amount is required" } : {}),
        ...(number(item.discount) > 100 ? { discount: "Use a percentage from 0 to 100" } : {}),
      }));
      return itemErrors.some((item) => Object.keys(item).length)
        ? { injectionItems: itemErrors }
        : {};
    },
    onSubmit: (values) => onSubmitAttachment({
      attachmentType: "injection",
      attachment: {
        ...values,
        injectionItems: values.injectionItems.map((item) => ({
          ...item,
          amount: number(item.amount),
          discount: number(item.discount),
          totalAmount: lineTotal(item),
        })),
      },
    }),
  });

  const items = formik.values.injectionItems || [];
  useEffect(() => {
    const totals = items.reduce((result, item) => ({
      fullAmount: result.fullAmount + number(item.amount),
      totalAmount: result.totalAmount + lineTotal(item),
    }), { fullAmount: 0, totalAmount: 0 });
    onPaymentChange?.((current) => ({
      ...current,
      fullAmount: Math.round(totals.fullAmount * 100) / 100,
      discount: 0,
      discountType: "percentage",
      totalAmount: Math.round(totals.totalAmount * 100) / 100,
    }));
  }, [items, onPaymentChange]);

  const updateItem = (index, field, value) => {
    formik.setFieldValue("injectionItems", items.map((item, itemIndex) =>
      itemIndex === index ? { ...item, [field]: value } : item));
  };

  return (
    <Box p={{ base: "2", md: "3" }} w="full">
      <form onSubmit={formik.handleSubmit}>
        <Text textAlign="center" fontSize="25px" fontWeight="700">Injection Form</Text>
        <SimpleGrid columns={{ base: 1, md: 2 }} my="5" spacing="4">
          <FormInput type="text" onChange={formik.handleChange("surname")} value={formik.values.surname} label="Surname" />
          <FormInput type="text" onChange={formik.handleChange("otherNames")} value={formik.values.otherNames} label="Other names" />
          <FormInput type="text" onChange={formik.handleChange("age")} value={formik.values.age} label="Age" />
          <FormInput type="text" onChange={formik.handleChange("unitNo")} value={formik.values.unitNo} label="Unit number" />
          <FormInput type="text" onChange={formik.handleChange("date")} value={formik.values.date} label="Date" />
          <FormInput type="text" onChange={formik.handleChange("physician")} value={formik.values.physician} label="Physician" />
        </SimpleGrid>

        <HStack justify="space-between" align="flex-start" mb="3">
          <Box>
            <Heading size="sm">Injection medications</Heading>
            <Text mt="1" fontSize="sm" color="gray.500">Select a listed medication or enter a new one, then add dosage and pricing.</Text>
          </Box>
          <Button type="button" size="sm" leftIcon={<AddIcon />} onClick={() => formik.setFieldValue("injectionItems", [...items, emptyInjection()])}>Add injection</Button>
        </HStack>

        <Box borderWidth="1px" borderRadius="xl" overflow="hidden">
          <Grid display={{ base: "none", lg: "grid" }} templateColumns="2fr 1.5fr 1fr .8fr 1fr 40px" gap="3" px="4" py="3" bg="gray.50">
            {['Drug', 'Dosage', 'Amount', 'Discount', 'Total amount', ''].map((label) => <Text key={label} fontSize="xs" fontWeight="800" color="gray.600" textTransform="uppercase">{label}</Text>)}
          </Grid>
          {items.map((item, index) => {
            const errors = formik.touched.injectionItems?.[index] ? formik.errors.injectionItems?.[index] || {} : {};
            return (
              <Grid key={index} templateColumns={{ base: "1fr", lg: "2fr 1.5fr 1fr .8fr 1fr 40px" }} gap="3" p="4" borderTopWidth={index > 0 ? "1px" : "0"} alignItems="start">
                <FormControl isInvalid={Boolean(errors.drug)}>
                  <FormLabel display={{ lg: "none" }}>Drug</FormLabel>
                  <CreatableSelect isClearable options={drugsList} value={item.drug ? { label: item.drug, value: item.drug } : null} onChange={(option) => updateItem(index, "drug", option?.value || "")} placeholder="Select or enter drug" styles={{ control: (base) => ({ ...base, minHeight: "40px" }), menuPortal: (base) => ({ ...base, zIndex: 1600 }) }} menuPortalTarget={document.body} />
                  <FormErrorMessage>{errors.drug}</FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={Boolean(errors.dosage)}>
                  <FormLabel display={{ lg: "none" }}>Dosage</FormLabel>
                  <Input value={item.dosage} onChange={(event) => updateItem(index, "dosage", event.target.value)} placeholder="e.g. 2 ml once daily" />
                  <FormErrorMessage>{errors.dosage}</FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={Boolean(errors.amount)}>
                  <FormLabel display={{ lg: "none" }}>Amount</FormLabel>
                  <MoneyInput value={item.amount} onChange={(value) => updateItem(index, "amount", value)} />
                  <FormErrorMessage>{errors.amount}</FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={Boolean(errors.discount)}>
                  <FormLabel display={{ lg: "none" }}>Discount</FormLabel>
                  <InputGroup><Input type="number" min="0" max="100" step="0.01" value={item.discount} onChange={(event) => updateItem(index, "discount", event.target.value)} /><InputRightAddon>%</InputRightAddon></InputGroup>
                  <FormErrorMessage>{errors.discount}</FormErrorMessage>
                </FormControl>
                <GridItem><FormLabel display={{ lg: "none" }}>Total amount</FormLabel><Text h="40px" display="flex" alignItems="center" fontWeight="800" color="brand.700">{currency(lineTotal(item))}</Text></GridItem>
                <IconButton type="button" aria-label={`Remove injection ${index + 1}`} icon={<DeleteIcon />} colorScheme="red" variant="ghost" isDisabled={items.length === 1} onClick={() => formik.setFieldValue("injectionItems", items.filter((_, itemIndex) => itemIndex !== index))} />
              </Grid>
            );
          })}
        </Box>

        <Button variant="outline" width="full" mt="6" type="submit">
          {formik.isSubmitting ? <CircularProgress isIndeterminate size="24px" color="teal" /> : "Attach"}
        </Button>
      </form>
    </Box>
  );
};

export default Injection;
